import clsx from 'clsx';
import type { StaticImageData } from 'next/image';
import Link from 'next/link';
import type { Dispatch, SetStateAction } from 'react';
import { useState } from 'react';
import { FaChevronDown, FaChevronRight } from 'react-icons/fa';

import type { CategoryType } from '../pages/tutorials';

type Media = string | StaticImageData;

export interface SimpleSideMenuItem<MediaType = unknown> {
  title: string;
  id: string;
  description: string;
  content: string;
  media: MediaType;
}

interface SimpleSideMenuProps<MediaType = unknown> {
  items: Array<SimpleSideMenuItem<MediaType>>;
  selected: number;
  setSelected?: Dispatch<SetStateAction<number>>;
}

interface DropDownSideMenuProps<MediaType = unknown> {
  items: Record<CategoryType, Array<SimpleSideMenuItem<MediaType>>>;
  selectedCategory: CategoryType;
  setSelectedCategory: Dispatch<SetStateAction<CategoryType>>;
  selected: number;
  setSelected: Dispatch<SetStateAction<number>>;
}

interface MobileMenuProps<MediaType = unknown> {
  items: Record<CategoryType, Array<SimpleSideMenuItem<MediaType>>>;
  selectedCategory: CategoryType;
  toggleCategory: (category: CategoryType) => void;
}

export function SimpleSideMenu<MediaType extends Media>({
  items,
  selected,
  setSelected,
}: SimpleSideMenuProps<MediaType>) {
  return (
    <div className="hidden px-8 lg:flex flex-col flex-nowrap">
      <div className="thinScrollbar overflow-y-auto pr-2">
        {items.map((item, index) => (
          <Link
            scroll={false}
            href={`#${encodeURIComponent(item.id)}`}
            key={item.id}
            onClick={() => (setSelected ? setSelected(index) : null)}
            className={clsx(
              'flex flex-col border-t-2 leading-6 border-primary-50/20 px-3 py-2 text-left text-nowrap',
              {
                'border-primary-50/0!': index === 0 || selected === index - 1,
                'rounded-lg border-primary-50/0! bg-primary-50/20':
                  selected === index,
              },
            )}
          >
            {item.title}
          </Link>
        ))}
      </div>
    </div>
  );
}

export function DropDownSideMenu<MediaType extends Media = Media>({
  items,
  selectedCategory,
  setSelectedCategory,
  selected,
  setSelected,
}: DropDownSideMenuProps<MediaType>) {
  const toggleCategory = (category: CategoryType) => {
    if (selectedCategory !== category) {
      setSelectedCategory(category);
      setSelected(0);
    }
  };

  return (
    <>
      <MobileMenu
        items={items}
        selectedCategory={selectedCategory}
        toggleCategory={toggleCategory}
      />
      <div className="noScrollbar hidden flex-col justify-between px-10 pb-6 lg:flex overflow-y-auto lg:pb-0">
        <div className="flex flex-col flex-nowrap gap-y-2">
          {Object.entries(items).map(([category]) => (
            <div key={category}>
              <Link
                scroll={false}
                href={`#${encodeURIComponent(
                  items[category as CategoryType][0].id,
                )}`}
                onClick={() => toggleCategory(category as CategoryType)}
                className={clsx(
                  'flex w-full justify-between px-3 py-2 text-left text-lg/7 font-medium text-primary-200 hover:text-white',
                  {
                    'text-white': selectedCategory === category,
                  },
                )}
              >
                <div>{category}</div>
                <div className="flex justify-end pl-4 pt-1.5">
                  {selectedCategory === category ? (
                    <FaChevronRight />
                  ) : (
                    <FaChevronDown />
                  )}
                </div>
              </Link>
              {selectedCategory === category &&
                items[category].map((item, index) => (
                  <Link
                    scroll={false}
                    href={`#${encodeURIComponent(item.id)}`}
                    key={item.id}
                    onClick={() => (setSelected ? setSelected(index) : null)}
                    className={clsx(
                      'ml-6 flex flex-col border-t-2 border-primary-50/20 px-3 py-2 text-left leading-6',
                      {
                        'border-primary-50/0!':
                          index === 0 || selected === index - 1,
                        'rounded-lg border-primary-50/0! bg-primary-50/20':
                          selected === index,
                      },
                    )}
                  >
                    {item.title}
                  </Link>
                ))}
            </div>
          ))}
        </div>
      </div>
    </>
  );
}

export default function MobileMenu({
  selectedCategory,
  items,
  toggleCategory,
}: MobileMenuProps) {
  const [menuOpen, setMenuOpen] = useState(false);

  const toggleMenu = () => {
    setMenuOpen(!menuOpen);
  };

  return (
    <div className="flex flex-col items-center lg:hidden">
      <div className="relative">
        <div
          onClick={toggleMenu}
          className={clsx(
            'flex cursor-pointer rounded-lg border-2 border-primary-50/20 bg-primary-50/20 px-4 py-2 text-lg font-semibold',
            { 'rounded-b-none': menuOpen },
          )}
        >
          {selectedCategory}
          <div className="flex justify-end pl-4 pt-1.5">
            {!menuOpen ? <FaChevronRight /> : <FaChevronDown />}
          </div>
        </div>
        <div className="flex w-full justify-center">
          {menuOpen ? (
            <div className="absolute font-semibold flex w-full flex-col divide-y-2 rounded-b-lg border-2 border-t-0 border-primary-50/20 bg-primary-700">
              {Object.entries(items).map(([category]) => (
                <Link
                  scroll={false}
                  key={category}
                  href={`#${encodeURIComponent(
                    items[category as CategoryType][0].id,
                  )}`}
                  onClick={() => {
                    toggleCategory(category as CategoryType);
                    toggleMenu();
                  }}
                  className={clsx('border-primary-50/20 px-4 py-2', {
                    'border-primary-50/0 bg-primary-50/10':
                      selectedCategory === category,
                  })}
                >
                  {category}
                </Link>
              ))}
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}
